<div class="edit-routine-dialog">
  <!-- Header -->
  <div class="dialog-header">
    <h2 class="dialog-title">Edit Routine</h2>
    <button mat-icon-button class="close-button" (click)="onCancel()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div class="dialog-content">
    <!-- Loading State -->
    @if (loading()) {
      <div class="loading-container">
        <mat-spinner diameter="32"></mat-spinner>
        <span class="loading-text">Loading routine data...</span>
      </div>
    }

    <!-- Error State -->
    @if (error() && !loading()) {
      <div class="error-container">
        <div class="error-message">{{ error() }}</div>
      </div>
    }

    <!-- Form -->
    @if (!loading() && !error()) {
      <form [formGroup]="routineForm" class="routine-form">
        <!-- Basic Information Section -->
        <div class="form-section">
          <h3 class="section-title">Basic Information</h3>
          <!-- Routine Name -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Routine Name</mat-label>
            <input
              matInput
              formControlName="name"
              placeholder="Enter routine name"
              maxlength="100"
            />
            <mat-hint align="end"
              >{{ routineForm.get("name")?.value?.length || 0 }}/100</mat-hint
            >
            @if (getFormFieldError("name")) {
              <mat-error>{{ getFormFieldError("name") }}</mat-error>
            }
          </mat-form-field>
          <!-- Description -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description (Optional)</mat-label>
            <textarea
              matInput
              formControlName="description"
              placeholder="Enter routine description"
              rows="3"
              maxlength="500"
            ></textarea>
            <mat-hint align="end"
              >{{
                routineForm.get("description")?.value?.length || 0
              }}/500</mat-hint
            >
            @if (getFormFieldError("description")) {
              <mat-error>{{ getFormFieldError("description") }}</mat-error>
            }
          </mat-form-field>
        </div>
        <mat-divider></mat-divider>
        <!-- Execution Settings Section -->
        <div class="form-section">
          <h3 class="section-title">Execution Settings</h3>
          <!-- HEAD Version Execution — only for manual (unscheduled) routines -->
          @if (
            !routineForm.get("cronScheduleType")?.value ||
            routineForm.get("cronScheduleType")?.value === "manual"
          ) {
            <div class="head-version-control">
              <mat-checkbox formControlName="useHead" color="primary">
                Execute against HEAD version (development)
              </mat-checkbox>
              <div class="status-hint">
                <mat-icon class="hint-icon">info</mat-icon>
                <span
                  >When enabled, manual runs use the current development version
                  instead of a published version</span
                >
              </div>
            </div>
          }
          <!-- Runs as User -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Runs as User</mat-label>
            <mat-select formControlName="runsAsId">
              @for (user of projectUsers(); track user.userId) {
                <mat-option [value]="user.userId">
                  <div class="user-option">
                    <div class="user-avatar">
                      <div class="avatar-fallback">
                        {{ (user.userName || "U").charAt(0) }}
                      </div>
                    </div>
                    <span>{{ user.userName || "Unknown User" }}</span>
                  </div>
                </mat-option>
              }
            </mat-select>
            @if (getFormFieldError("runsAsId")) {
              <mat-error>{{ getFormFieldError("runsAsId") }}</mat-error>
            }
          </mat-form-field>
          <!-- Workflow (SCRUM-698) -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Workflow</mat-label>
            <mat-select formControlName="workflowId">
              <mat-select-trigger>
                {{
                  getWorkflowNameById(routineForm.get("workflowId")?.value) ||
                    "Select workflow"
                }}
              </mat-select-trigger>
              @for (workflow of projectWorkflows; track workflow.workflowId) {
                <mat-option [value]="workflow.workflowId">
                  <span>{{ workflow.name }}</span>
                </mat-option>
              }
            </mat-select>
            @if (getFormFieldError("workflowId")) {
              <mat-error>{{ getFormFieldError("workflowId") }}</mat-error>
            }
          </mat-form-field>
        </div>
        <mat-divider></mat-divider>
        <!-- Trigger Source Section -->
        <div class="form-section">
          <h3 class="section-title">Trigger Source</h3>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Trigger Source</mat-label>
            <mat-select
              formControlName="source"
              (selectionChange)="onSourceChange($event)"
            >
              @for (option of sourceOptions; track option.value) {
                <mat-option [value]="option.value">
                  {{ option.label }}
                </mat-option>
              }
            </mat-select>
            <mat-icon matSuffix>bolt</mat-icon>
          </mat-form-field>
        </div>
        <mat-divider></mat-divider>
        <!-- Schedule Section (hidden when webhook) -->
        @if (!isWebhookSource()) {
          <div class="form-section">
            <h3 class="section-title">Schedule</h3>
            <!-- Schedule Type Selection -->
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Schedule Type</mat-label>
              <mat-select
                formControlName="cronScheduleType"
                (selectionChange)="onCronScheduleChange($event)"
              >
                @for (option of cronScheduleOptions; track option.value) {
                  <mat-option [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <!-- Custom Cron Input -->
            @if (showCustomCronInput) {
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Custom Cron Expression</mat-label>
                <input
                  matInput
                  formControlName="crontab"
                  placeholder="e.g., 0 2 * * * (daily at 2 AM)"
                  [class.error]="
                    routineForm.get('crontab')?.value &&
                    !validateCustomCron(routineForm.get('crontab')?.value)
                  "
                />
                <mat-hint
                  >Format: minute hour day month weekday (e.g., "0 2 * * *" for
                  daily at 2 AM)</mat-hint
                >
                @if (
                  routineForm.get("crontab")?.value &&
                  !validateCustomCron(routineForm.get("crontab")?.value)
                ) {
                  <mat-error>
                    {{
                      getCronValidationError(
                        routineForm.get("crontab")?.value
                      ) || "Invalid cron expression format"
                    }}
                  </mat-error>
                }
              </mat-form-field>
            }
            <!-- Schedule Preview -->
            @if (routineForm.get("crontab")?.value) {
              <div class="schedule-preview">
                <mat-icon class="preview-icon">schedule</mat-icon>
                <span class="preview-text">{{
                  getSelectedCronScheduleLabel()
                }}</span>
              </div>
            }
          </div>
          <mat-divider></mat-divider>
        }
        <!-- Webhook Configuration Section -->
        @if (isWebhookSource()) {
          <div class="form-section">
            <h3 class="section-title">Webhook Configuration</h3>
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Webhook Type</mat-label>
              <mat-select formControlName="webhookType">
                @for (option of webhookTypeOptions; track option.value) {
                  <mat-option [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                }
              </mat-select>
              <mat-icon matSuffix>webhook</mat-icon>
            </mat-form-field>
            @if (getFormFieldError("webhookType")) {
              <mat-error>{{ getFormFieldError("webhookType") }}</mat-error>
            }
            <!-- Registry-backed Box webhooks resolve signature keys from the
                 Box connection's app — no manual secrets needed (SCRUM-636). -->
            @if (boxSecretsOptional()) {
              <div class="status-hint">
                <mat-icon class="hint-icon">verified_user</mat-icon>
                <span
                  >Signature keys are provided by the project's Box connection.
                  Save the routine, then use "Set Up Box Webhook" below to
                  provision it.</span
                >
              </div>
            }
            @if (!boxSecretsOptional() || hasLegacyWebhookSecrets()) {
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{
                  boxSecretsOptional()
                    ? "Webhook Primary Secret (Legacy Fallback)"
                    : "Webhook Primary Secret"
                }}</mat-label>
                <mat-select formControlName="webhookSecretId1">
                  @for (secret of availableSecrets(); track secret.secretId) {
                    <mat-option [value]="secret.secretId">
                      {{ secret.name }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>key</mat-icon>
              </mat-form-field>
              @if (getFormFieldError("webhookSecretId1")) {
                <mat-error>{{
                  getFormFieldError("webhookSecretId1")
                }}</mat-error>
              }
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{
                  boxSecretsOptional()
                    ? "Webhook Secondary Secret (Legacy Fallback)"
                    : "Webhook Secondary Secret (Optional)"
                }}</mat-label>
                <mat-select formControlName="webhookSecretId2">
                  <mat-option [value]="null">None</mat-option>
                  @for (secret of availableSecrets(); track secret.secretId) {
                    <mat-option [value]="secret.secretId">
                      {{ secret.name }}
                    </mat-option>
                  }
                </mat-select>
                <mat-icon matSuffix>key</mat-icon>
              </mat-form-field>
            }
            <!-- Box webhook auto-provisioning (SCRUM-614) -->
            @if (canSetUpBoxWebhook()) {
              <button
                type="button"
                mat-stroked-button
                color="primary"
                (click)="openBoxWebhookSetup()"
              >
                <mat-icon>webhook</mat-icon>
                Set Up Box Webhook
              </button>
            }
          </div>
          <mat-divider></mat-divider>
        }
        <!-- Arguments Section -->
        <div class="form-section">
          <h3 class="section-title">Arguments (JSON)</h3>
          <div class="status-hint">
            <mat-icon class="hint-icon">info</mat-icon>
            <span
              >Merged into every job's arguments: schema defaults &lt; routine
              arguments &lt; per-run arguments</span
            >
          </div>
          <app-code-editor
            [content]="argumentsContent"
            (updateContent)="onArgumentsContentChange($event)"
            (startEdit)="onArgumentsStartEdit()"
            mode="json"
            [minimumHeight]="120"
            class="full-width"
          >
          </app-code-editor>
          @if (getFormFieldError("arguments")) {
            <div class="field-error">{{ getFormFieldError("arguments") }}</div>
          }
        </div>
        <mat-divider></mat-divider>
        <!-- Status Section -->
        <div class="form-section">
          <h3 class="section-title">Status</h3>
          <div class="status-control">
            <mat-checkbox formControlName="paused" class="pause-checkbox">
              Pause routine (prevents scheduled executions)
            </mat-checkbox>
            <div class="status-hint">
              <mat-icon class="hint-icon">info</mat-icon>
              <span
                >Paused routines will not execute automatically but can still be
                run manually</span
              >
            </div>
          </div>
        </div>
      </form>
    }
  </div>

  <!-- Actions -->
  @if (!loading()) {
    <div class="dialog-actions">
      <button mat-button (click)="onCancel()" [disabled]="loading()">
        Cancel
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="onSave()"
        [disabled]="loading() || routineForm.invalid"
      >
        @if (loading()) {
          <mat-spinner diameter="16"></mat-spinner>
        }
        @if (!loading()) {
          <span>Save Changes</span>
        }
        @if (loading()) {
          <span>Saving...</span>
        }
      </button>
    </div>
  }
</div>
